import React, { useEffect, useState } from "react";
import { Box, Fade, Stack } from "@mui/material";
import { SegmentSizeTooltip, SegmentSubTitle } from "../SegmentSettings.styled";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SegmentTitleTextField } from "../../ContentBlocksEditor/children/ContentBlocksHeader/ContentBlocksHeader.styled";
import { radius, spacing } from "../../../utils";
import { useDispatch, useSelector } from "react-redux";
import {
  getSelectedSegment,
  getWheelSegments,
} from "../../../redux/selectors/segmentBuilderSelectors";
import { setWheelSegments } from "../../../redux/reducers/segmentBuilder";
import { withHistory } from "../../../redux/actions/history";
import { MIN_SPAN_PCT } from "../../SegmentBuilder/resize/segmentResizeMath";
import { applySegmentSizeChange } from "../../SegmentBuilder/resize/segmentResizeDistribute";
import SettingsSlider from "../../SegmentBuilder/Shared/SettingsSlider";

const ACCENT = "#9B5AF8";
// gordon-0925-P1-edit-wheel-segment-size-click-redirects-to-resize-pane: the
// slider's own drag handle must clear the >=32px touch-target floor. This is a
// literal-style override (SettingsSlider forwards an unrecognised `handleStyle`
// prop straight onto rc-slider's handle, last in JSX so it wins over the
// component's own 24x24 default -- see SettingsSlider.thumbTouchTarget.test.jsx,
// which pins that 24x24 DEFAULT for every OTHER caller; this override is local
// to this one usage and changes nothing there). marginTop keeps the handle
// centred on the 8px-tall track, matching the -8 the 24px default uses.
const SEGMENT_SIZE_SLIDER_HANDLE_PX = 32;
const SEGMENT_SIZE_SLIDER_HANDLE_MARGIN_TOP =
  -(SEGMENT_SIZE_SLIDER_HANDLE_PX - 8) / 2;
const segmentSizeHandleStyle = {
  borderColor: ACCENT,
  backgroundColor: ACCENT,
  height: SEGMENT_SIZE_SLIDER_HANDLE_PX,
  width: SEGMENT_SIZE_SLIDER_HANDLE_PX,
  marginTop: SEGMENT_SIZE_SLIDER_HANDLE_MARGIN_TOP,
  marginLeft: "10px",
};
const segmentSizeHandleStyleDisabled = {
  ...segmentSizeHandleStyle,
  borderColor: "#e9e9e9",
  backgroundColor: "#e9e9e9",
};

// Segment Size is a PERCENTAGE of the wheel: the app's canonical floor is
// MIN_SPAN_PCT (1) and the ceiling is 100. clampSegmentSize is the single
// validator for the typed field (QA-1042).
export const MAX_SEGMENT_SIZE = 100;

// fix-ew-remaining-ar0908-4: the typed-value box was a hardcoded 56px,
// independent of what it renders. segmentResizeDistribute's redistribution
// math commits 2-decimal values (e.g. "66.67") that are wider than 56px can
// hold — live-measured on web-dev.iyuga.net (main.c7829ef7.js): scrollWidth 63
// vs clientWidth 56, the tail of the number silently scrolled out of view.
// Size the box to the value it's currently showing instead; 56px stays the
// floor for the common 1-3 digit case.
const SEGMENT_SIZE_BOX_MIN_WIDTH_PX = 56;
const SEGMENT_SIZE_BOX_CHAR_WIDTH_PX = 8; // approx digit width, DM Sans 12px/500
const SEGMENT_SIZE_BOX_PADDING_PX = 30; // matches "& input" padding: 0px 15px
export const computeSegmentSizeBoxWidthPx = (rawValue) => {
  const text = rawValue === null || rawValue === undefined ? "" : String(rawValue);
  const needed = text.length * SEGMENT_SIZE_BOX_CHAR_WIDTH_PX + SEGMENT_SIZE_BOX_PADDING_PX;
  return Math.max(SEGMENT_SIZE_BOX_MIN_WIDTH_PX, needed);
};

// vy-0914-ew-segment-size-raw-float-and-clip-1024: an even N-way split (e.g. a
// 3-segment wheel, 100/3) can hand this field a repeating-decimal value like
// "33.333333333333336" (17 chars). computeSegmentSizeBoxWidthPx widens the box
// to fit that whole string, but the Segment Settings pane isn't wide enough to
// honor it below ~1200px -- live-measured on web-dev.iyuga.net (main.56132db7.js,
// wheel 6232): at 1024x768 the box clips (clientWidth 115 vs scrollWidth 154)
// and the "Segment Size" label wraps mid-word. Rounding the DISPLAYED value to
// 2 decimals (matching the round2 convention every other segment-value mutation
// path already commits -- segmentResizeMath.js, segmentResizeDistribute.js,
// SegmentsTab/index.jsx) keeps the box's natural width small at every viewport
// without touching the underlying stored precision.
export const roundSegmentSizeForDisplay = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return rawValue;
  const num = Number(rawValue);
  if (!Number.isFinite(num)) return rawValue;
  return String(Math.round(num * 100) / 100);
};

export const clampSegmentSize = (raw) => {
  // Empty / whitespace: no commit, so the field can be cleared and retyped
  // (never commit a 0-width / empty size).
  if (typeof raw === "string" && raw.trim() === "") return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null; // non-numeric -> no commit
  // Floor at MIN_SPAN_PCT (a 0 collapses the segment to an unclickable sliver);
  // ceiling 100 (percentage). This also makes 100 typeable — the old
  // `value <= 100` guard silently DROPPED any keystroke that would exceed 100.
  return Math.min(MAX_SEGMENT_SIZE, Math.max(MIN_SPAN_PCT, num));
};

// A bare digit/decimal-point string, or empty (so the field can be cleared
// mid-edit) -- anything else (a letter, a paste of "12x") is refused outright
// rather than landing in the draft.
const DRAFT_PATTERN = /^\d*\.?\d*$/;

// gordon-0925-P1-edit-wheel-segment-size-click-redirects-to-resize-pane:
// clicking this row used to dispatch `setSegmentResizeView(true)`, swapping
// the ENTIRE Segment Settings pane out for the separate per-Segment Resize
// view (V1) just to expose a slider next to the number box -- Gordon: "jumps
// to the separate Wheel Segment Resizing pane - awkward". The row never
// navigates now; the number box + slider below sit in place, and this exact
// component (not a copy) is reused inside SegmentResizeView (V1) so the two
// panes render byte-identical size controls (`hideInfoTooltip` only silences
// the redundant (i) icon there, where the "Resize Segment" heading above
// already explains the control -- see SegmentResizeView.jsx).
const SegmentSize = ({ hideInfoTooltip = false }) => {
  const dispatch = useDispatch();
  const selectedSegment = useSelector(getSelectedSegment);
  const wheelData = useSelector(getWheelSegments) || [];

  // editwheel-0922-P2-segment-size-input-commits-every-keystroke: this field
  // used to be fully controlled (value={displaySize}, dispatch on every
  // keystroke). pages/SegmentBuilder/index.jsx's sum-to-100 correction
  // useEffect fires on every wheel_segments change and proportionally
  // redistributes ALL unlocked segments -- including the one mid-edit -- so
  // typing "30" landed on "3.03" before the second digit even arrived; 100
  // was likewise untypeable. `draft` holds the in-progress text while the
  // field has focus; the store (and therefore that correction effect) is
  // only touched once, on blur or Enter, via the same clampSegmentSize this
  // field already used. `null` means "not currently being edited" -- show
  // the committed, renormalised store value.
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    // A different Segment was selected (or this one re-hydrated) while a
    // draft was in progress -- drop it rather than committing a stale edit
    // onto the newly-selected Segment or leaking it across the switch.
    setDraft(null);
  }, [selectedSegment?.id]);

  const handleChange = (event) => {
    const raw = event.target.value;
    if (raw !== "" && !DRAFT_PATTERN.test(raw)) return; // refuse letters etc.
    setDraft(raw);
  };

  const commitDraft = () => {
    if (draft === null) return;
    // QA-1042: enforce the app's segment-size range [MIN_SPAN_PCT..100]. The old
    // guard (`newValue <= 100` on the raw string) accepted 0 — collapsing the
    // segment to a 0-width, unclickable sliver that still held its content — and
    // silently swallowed any keystroke pushing the value over 100 (so 100 was
    // effectively untypeable). clampSegmentSize rejects an empty/non-numeric entry
    // (no commit) and clamps everything else into range.
    const clamped = clampSegmentSize(draft);
    setDraft(null);
    if (clamped === null) return;
    // gordon-0923-P1-fe5996-segment-size-edit-reverts-on-blur: dispatching a
    // bare single-segment `updateSegmentValue` left every OTHER segment's
    // value untouched, so the wheel no longer summed to 100. The very next
    // render, `pages/SegmentBuilder/index.jsx`'s sum-to-100 correction effect
    // (fires on every `wheel_segments` change) saw that drift and
    // proportionally redistributed ALL unlocked segments back toward 100 —
    // including the one just committed — silently overwriting the typed
    // value back to (approximately) its pre-edit baseline on blur/Enter/
    // click-away. applySegmentSizeChange sets THIS segment to the typed value
    // exactly and absorbs the difference across the other unlocked segments
    // (same math the V1/V2 sliders already commit through), so the wheel
    // already sums to 100 by the time that correction effect runs and has
    // nothing left to "fix".
    const nextSegments = applySegmentSizeChange(
      wheelData,
      selectedSegment?.id,
      clamped
    );
    dispatch(withHistory(setWheelSegments(nextSegments)));
  };

  const handleBlur = () => commitDraft();

  const handleKeyDown = (event) => {
    if (event.key === "Enter") commitDraft();
  };

  // Slider drag: the SAME commit path as the typed box (applySegmentSizeChange),
  // so dragging and typing always leave the wheel in an identical, already
  // renormalised state -- "the other segments keep adjusting exactly as the
  // Resize pane does today" (Gordon 2026-09-25). Live-updates while dragging
  // AND on release, matching every other block-settings slider (GORDON LAW
  // 2026-09-15 03:25 PT).
  const handleSliderChange = (newValue) => {
    if (selectedSegment?.id == null) return;
    const clamped = clampSegmentSize(newValue);
    if (clamped === null) return;
    dispatch(
      withHistory(
        setWheelSegments(applySegmentSizeChange(wheelData, selectedSegment.id, clamped))
      )
    );
  };

  const displaySize = roundSegmentSizeForDisplay(selectedSegment?.value);
  const fieldValue = draft === null ? displaySize : draft;
  const unlockedSegments = wheelData.filter((segment) => !segment.resize_lock);
  const isSliderDisabled =
    !!selectedSegment?.resize_lock || unlockedSegments.length <= 1;

  return (
    <Box
      sx={{
        // Corner-rounded highlight + text↔edge margin (never square/flush),
        // consistent with the accordion + tab highlights.
        padding: `10px ${spacing.md}px`,
        borderRadius: `${radius.md}px`,
      }}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack direction={"row"} alignItems={"center"} gap={"15px"}>
          <SegmentSubTitle>Segment Size</SegmentSubTitle>
          <SegmentTitleTextField
            // autoFocus
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            inputProps={{
              "aria-label": "Segment Size",
            }}
            sx={{ width: `${computeSegmentSizeBoxWidthPx(fieldValue)}px` }}
            value={fieldValue}
            name="segmentSize"
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            fullWidth
            fontWeightValue="500"
            fontSizeValue="12px"
            backgroundColor="#F6F7F9"
          />
        </Stack>

        {/* gordon-0925-P1-...: hidden inside V1 (SegmentResizeView passes
            hideInfoTooltip) -- the "Resize Segment" heading immediately above
            it there already explains the control, same precedent as bug
            #504-rv-590ac68e's redundant-icon removals elsewhere in V1. */}
        {!hideInfoTooltip && (
          <SegmentSizeTooltip
            title="Adjust the size of the Segment. Default size is 1"
            placement="left"
            arrow
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 600 }}
          >
            <InfoOutlinedIcon sx={{ color: "#123051", cursor: "pointer" }} />
          </SegmentSizeTooltip>
        )}
      </Stack>

      {/* The slider -- same shared control, same commit path as the number
          box above. Disabled (like the V1/V2 per-Segment resize cards) when
          this Segment is locked or it's the only unlocked Segment left (no
          other Segment to absorb the difference). */}
      <SettingsSlider
        sliderValue={selectedSegment?.value}
        onValueChange={handleSliderChange}
        title=""
        min={MIN_SPAN_PCT}
        max={MAX_SEGMENT_SIZE}
        step={0.5}
        hideValueInput
        containerStyle={{ width: "100%", margin: "0px", padding: "0px" }}
        fullSlider
        disabled={isSliderDisabled}
        handleStyle={
          isSliderDisabled ? segmentSizeHandleStyleDisabled : segmentSizeHandleStyle
        }
        ariaLabelForHandle="Segment Size slider"
      />
    </Box>
  );
};

export default SegmentSize;
